// The person's GitHub account, as the app sees it: linked or not, with which
// right on the process repository. Shown in the profile menu and in the
// répertoire header; the diagram toolbar reuses the hook and the connect URL.
import { useCallback, useEffect, useState } from 'react';
import { GitBranch, RefreshCw, Unlink } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';

export interface GithubMe {
  app: { configured: boolean; slug: string | null; installed: boolean };
  write_mode: 'user' | 'server' | 'none';
  linked: boolean;
  can_write: boolean;
  identity: {
    login: string; name: string | null; avatar_url: string | null; html_url: string | null;
    permission: 'admin' | 'write' | 'read' | 'none'; can_write: boolean; status: 'ok' | 'expired'; connected_at: string;
  } | null;
  repo: { owner: string; name: string; branch: string; full_name: string; url: string };
}

export function useGithubMe() {
  const [me, setMe] = useState<GithubMe | null>(null);
  const reload = useCallback(() => api.getGithubMe().then(setMe).catch(() => setMe(null)), []);
  useEffect(() => { reload(); }, [reload]);
  return { me, reload };
}

/** Where "link my account" sends the browser; comes back to the given hash (default: here). */
export const githubConnectUrl = (returnTo?: string) =>
  `/api/github/connect?return=${encodeURIComponent(returnTo ?? (window.location.hash || '#/repository'))}`;

export function PermissionBadge({ identity }: { identity: NonNullable<GithubMe['identity']> }) {
  const { t } = useT();
  if (identity.status === 'expired') return <span className="badge badge-err">{t('gh.status.expired')}</span>;
  const cls = identity.permission === 'admin' || identity.permission === 'write' ? 'badge-ok'
    : identity.permission === 'read' ? 'badge-warn' : 'badge-err';
  return <span className={`badge ${cls}`}>{t(`gh.perm.${identity.permission}`)}</span>;
}

/**
 * variant "menu"   — rows inside the profile dropdown
 * variant "inline" — one compact line (page headers)
 * Renders nothing unless writes go through personal accounts (GitHub App mode).
 */
export function GithubAccount({ variant }: { variant: 'menu' | 'inline' }) {
  const { t } = useT();
  const { me, reload } = useGithubMe();
  const [busy, setBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  // the callback reports a failed link through the hash: #/repository?github=error&reason=…
  useEffect(() => {
    if (variant !== 'inline') return;
    const [path, query] = window.location.hash.split('?');
    const params = new URLSearchParams(query ?? '');
    if (params.get('github') === 'error') {
      setLinkError(params.get('reason') ?? '?');
      history.replaceState(null, '', path);
    }
  }, [variant]);

  if (!me || me.write_mode !== 'user') return null;

  const recheck = async () => {
    setBusy(true);
    try { await api.recheckGithub(); await reload(); } finally { setBusy(false); }
  };
  const unlink = async () => {
    setBusy(true);
    try { await api.unlinkGithub(); await reload(); } finally { setBusy(false); }
  };

  const identity = me.identity;

  if (variant === 'menu') {
    return (
      <div className="profile-menu-github">
        {identity ? (
          <>
            <div className="gh-account">
              {identity.avatar_url ? <img className="gh-avatar" src={identity.avatar_url} alt="" /> : <GitBranch size={14} />}
              <a className="gh-login" href={identity.html_url ?? me.repo.url} target="_blank" rel="noreferrer">@{identity.login}</a>
              <PermissionBadge identity={identity} />
            </div>
            <div className="flex gap-8">
              {identity.status === 'expired'
                ? <a className="btn btn-accent btn-sm" href={githubConnectUrl()}>{t('gh.link')}</a>
                : <button className="btn btn-sm" disabled={busy} onClick={recheck}><RefreshCw className={`icon-sm${busy ? ' icon-spin' : ''}`} /> {t('gh.recheck')}</button>}
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={unlink}><Unlink className="icon-sm" /> {t('gh.unlink')}</button>
            </div>
          </>
        ) : (
          <>
            <div className="text-xs text-ink-3">{t('gh.hint.notLinked')}</div>
            <a className="btn btn-accent btn-sm" href={githubConnectUrl()}><GitBranch className="icon-sm" /> {t('gh.link')}</a>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="gh-account">
      {linkError && <span className="text-xs" style={{ color: 'var(--err)' }}>{t('gh.error.linkFailed', { reason: linkError })}</span>}
      {identity ? (
        <>
          {identity.avatar_url ? <img className="gh-avatar" src={identity.avatar_url} alt="" /> : <GitBranch size={14} />}
          <a className="gh-login" href={identity.html_url ?? me.repo.url} target="_blank" rel="noreferrer">@{identity.login}</a>
          <PermissionBadge identity={identity} />
          {identity.status === 'expired' && <a className="btn btn-sm" href={githubConnectUrl()}>{t('gh.link')}</a>}
          {identity.status === 'ok' && !identity.can_write && (
            <button className="btn btn-sm" disabled={busy} onClick={recheck}><RefreshCw className={`icon-sm${busy ? ' icon-spin' : ''}`} /> {t('gh.recheck')}</button>
          )}
        </>
      ) : (
        <a className="btn btn-sm" href={githubConnectUrl()} title={t('gh.hint.notLinked')}><GitBranch className="icon-sm" /> {t('gh.link')}</a>
      )}
    </div>
  );
}
